import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { Role, Permission } from '../types/models';

export interface CreateRoleData {
  name: string;
  description?: string;
  permission_ids?: number[];
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  permissions?: number[];
}

class AuthorizationService {
  private rolesBaseUrl = '/admin/county/roles';
  private permissionsBaseUrl = '/admin/county/permissions';

  // Role endpoints
  async getRoles(page?: number): Promise<ApiResponse<PaginatedResponse<Role>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Role>>(`${this.rolesBaseUrl}${params}`);
  }

  async getRole(id: number): Promise<ApiResponse<Role>> {
    return apiClient.get<Role>(`${this.rolesBaseUrl}/${id}`);
  }

  async createRole(data: CreateRoleData): Promise<ApiResponse<Role>> {
    return apiClient.post<Role>(this.rolesBaseUrl, data);
  }

  async updateRole(id: number, data: UpdateRoleData): Promise<ApiResponse<Role>> {
    return apiClient.put<Role>(`${this.rolesBaseUrl}/${id}`, data);
  }

  async deleteRole(id: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.rolesBaseUrl}/${id}`);
  }

  async assignPermissions(roleId: number, permissionIds: number[]): Promise<ApiResponse<Role>> {
    return apiClient.post<Role>(`${this.rolesBaseUrl}/${roleId}/permissions`, { permission_ids: permissionIds });
  }

  async syncPermissions(roleId: number, permissionIds: number[]): Promise<ApiResponse<Role>> {
    return apiClient.put<Role>(`${this.rolesBaseUrl}/${roleId}/permissions`, { permission_ids: permissionIds });
  }

  async removePermission(roleId: number, permissionId: number): Promise<ApiResponse<Role>> {
    return apiClient.delete(`${this.rolesBaseUrl}/${roleId}/permissions/${permissionId}`);
  }

  async getRolePermissions(roleId: number): Promise<ApiResponse<Permission[]>> {
    return apiClient.get<Permission[]>(`${this.rolesBaseUrl}/${roleId}/permissions`);
  }

  // Permission endpoints
  async getPermissions(page?: number): Promise<ApiResponse<PaginatedResponse<Permission>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Permission>>(`${this.permissionsBaseUrl}${params}`);
  }

  async getAllPermissions(): Promise<ApiResponse<Permission[]>> {
    return apiClient.get<Permission[]>(`${this.permissionsBaseUrl}/all`);
  }

  async getPermission(id: number): Promise<ApiResponse<Permission>> {
    return apiClient.get<Permission>(`${this.permissionsBaseUrl}/${id}`);
  }

  async getPermissionsByModule(module: string): Promise<ApiResponse<Permission[]>> {
    return apiClient.get<Permission[]>(`${this.permissionsBaseUrl}/module/${module}`);
  }
}

export const authorizationService = new AuthorizationService();
export default authorizationService;
